<template>
    <b-card
        :title="translations.customerDetails[this.lang]"
        @cancel="closeModal()"
        @submit="saveCustomerDetails({ userAccountId, customerData })"
        :error-message="errorMessage"
        :loading="loading"
        :disabled="!customerData.country_id"
        :submitBtn="!!userAccountId"
        :submit-text="{ el: 'Αποθήκευση', en: 'Save', it: 'Salva' }"
        :cancel-text="{ el: 'Κλείσιμο', en: 'Close', it: 'Chiudere' }"
    >
        <v-row no-gutters>
            <v-col cols="12" class="d-flex flex-row justify-center mb-3">
                <v-col cols="6">
                    <b-select
                        style="border-top-right-radius: 0;border-bottom-right-radius: 0; margin-right: 10px;"
                        :items="countryCodes"
                        item-value="country_id"
                        item-text="countryCode"
                        :label="translations.cc[lang]"
                        hint="Country's dial code"
                        v-model="customerData.country_id"
                        :success="!!customerData.country_id"
                        :error-messages="errorMsg.country"
                        @focus="errorMsg.country = ''"
                        @blur="validateCountry"
                        :disabled="isCustomer"
                    ></b-select>
                </v-col>
                <v-col cols="6">
                    <b-text-field
                        style="border-top-left-radius: 0;border-bottom-left-radius: 0;"
                        :label="translations.mobile[lang]"
                        hint="ex: 6912345780"
                        v-model="customerData.mobile"
                        v-mask="'##########'"
                        disabled
                    ></b-text-field>
                </v-col>
            </v-col>

            <hr
                style="height: 1px; width: 100%; margin-bottom: 20px; opacity: 20%;"
            />

            <v-col cols="12" class="mb-4">
                <v-menu
                    :close-on-content-click="true"
                    offset-y
                    max-width="290px"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            :label="translations.formDate[lang]"
                            color="secondary"
                            outlined
                            dense
                            clearable
                            hide-details
                            v-on="on"
                            prepend-icon="mdi-calendar"
                            v-bind="attrs"
                            v-model="dateFormatted"
                        ></v-text-field>
                    </template>

                    <v-date-picker
                        color="secondary"
                        no-title
                        v-model="customerData.form_date"
                        :max="new Date().toISOString().substring(0, 11)"
                    ></v-date-picker>
                </v-menu>
            </v-col>

            <v-col cols="6" class="pr-2">
                <b-text-field
                    :label="translations.firstName[lang]"
                    v-model="customerData.first_name"
                ></b-text-field>
            </v-col>

            <v-col cols="6">
                <b-text-field
                    :label="translations.lastName[lang]"
                    v-model="customerData.last_name"
                ></b-text-field>
            </v-col>

            <v-col cols="6" class="pr-2">
                <b-text-field
                    label="Email"
                    hint="ex: customer@mail.com"
                    v-model="customerData.email"
                ></b-text-field>
            </v-col>

            <v-col cols="6">
                <b-text-field
                    :label="translations.landlinePhone[lang]"
                    v-model="customerData.phone"
                    hint="ex: 2103456789"
                ></b-text-field>
            </v-col>

            <v-col cols="6" class="mb-4 mt-3 pr-2">
                <v-menu
                    :close-on-content-click="false"
                    offset-y
                    max-width="290px"
                    ref="menu"
                    v-model="menu"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            :label="translations.birthDate[lang]"
                            color="secondary"
                            outlined
                            dense
                            clearable
                            hide-details
                            v-bind="attrs"
                            v-on="on"
                            :value="
                                customerData.birth_date
                                    ? moment(customerData.birth_date).format(
                                          'DD-MM-YYYY'
                                      )
                                    : ''
                            "
                        ></v-text-field>
                    </template>

                    <v-date-picker
                        color="secondary"
                        no-title
                        v-model="customerData.birth_date"
                        ref="picker"
                        :active-picker.sync="activePicker"
                        :locale="dateLang"
                        :max="new Date().toISOString().substring(0, 10)"
                        :picker-date="pickerDate"
                        min="1930-01-01"
                        @change="save"
                    ></v-date-picker>
                </v-menu>
            </v-col>

            <v-col cols="6">
                <b-select
                    :items="genders"
                    :item-text="`name[${lang}]`"
                    item-value="value"
                    v-model="customerData.gender"
                    :label="translations.gender[lang]"
                ></b-select>
            </v-col>

            <hr
                style="height: 1px; width: 100%; margin-bottom: 20px; opacity: 20%;"
            />

            <v-col cols="12">
                <Places
                    :label="translations.address[lang]"
                    class="mt-3"
                    outlined
                    dense
                    hide-details="auto"
                    @newAddress="setAddressDetails"
                    :clearable="false"
                    hint="The customer's residential address"
                    ref="places"
                />
            </v-col>

            <v-col cols="6" class="pr-2">
                <b-text-field
                    :label="translations.postCode[lang]"
                    v-model="customerData.zipcode"
                ></b-text-field>
            </v-col>

            <v-col cols="6">
                <b-select
                    :items="counties"
                    item-text="name"
                    item-value="county_id"
                    :disabled="!customerData.country_id"
                    :label="translations.country[lang]"
                    v-model="customerData.county_id"
                ></b-select>
            </v-col>
            <hr
                style="height: 1px; width: 100%; margin-bottom: 10px; margin-top: 20px; opacity: 20%;"
            />
        </v-row>
        <v-row no-gutters>
            <v-col cols="12">
                <span class="text-h6 pa-3">{{
                    translations.customAttributes[lang]
                }}</span>
            </v-col>

            <v-col
                v-for="(attribute, index) in activeAttributes"
                :key="index"
                cols="6"
                class="pa-2"
            >
                <div>
                    <b-text-field
                        v-if="attribute.attribute_type == 'text'"
                        :label="attribute.name"
                        v-model="attribute.values[0]"
                        no-margin-top
                        :error-messages="
                            attribute.values[0] &&
                            attribute.value_type == 'number' &&
                            isNaN(attribute.values[0])
                                ? translations.invalidValue[lang]
                                : ''
                        "
                        @input="
                            attachExtraAttribute(
                                attribute.user_account_attribute_definition_id,
                                attribute.values,
                                true,
                                attribute.values[0] &&
                                    attribute.value_type == 'number' &&
                                    isNaN(attribute.values[0])
                            )
                        "
                    ></b-text-field>
                    <b-select
                        v-if="attribute.attribute_type == 'select'"
                        :items="
                            attribute.user_account_attribute_definition_values
                        "
                        :label="attribute.name"
                        item-text="value"
                        item-value="user_account_attribute_definition_value_id"
                        v-model="attribute.values[0]"
                        @change="
                            attachExtraAttribute(
                                attribute.user_account_attribute_definition_id,
                                attribute.values
                            )
                        "
                    ></b-select>
                    <b-select
                        v-if="attribute.attribute_type == 'multi_select'"
                        :items="
                            attribute.user_account_attribute_definition_values
                        "
                        :label="attribute.name"
                        multiple
                        item-text="value"
                        item-value="user_account_attribute_definition_value_id"
                        v-model="attribute.values"
                        @change="
                            attachExtraAttribute(
                                attribute.user_account_attribute_definition_id,
                                attribute.values
                            )
                        "
                    ></b-select>
                    <v-radio-group
                        v-if="attribute.attribute_type == 'radio'"
                        class="mt-2 pt-0"
                        hide-details
                        v-model="attribute.values[0]"
                        @change="
                            attachExtraAttribute(
                                attribute.user_account_attribute_definition_id,
                                attribute.values
                            )
                        "
                    >
                        <v-row no-gutters class="d-flex justify-center">
                            <v-col
                                cols="12"
                                md="12"
                                class="d-flex justify-start pa-1"
                                ><span class="text-body-2"
                                    >{{ attribute.name }}:</span
                                ></v-col
                            >
                            <v-col
                                v-for="option in attribute.user_account_attribute_definition_values"
                                :key="
                                    option.user_account_attribute_definition_value_id
                                "
                                cols="auto"
                                class="pr-2 mr-2"
                            >
                                <v-radio
                                    color="secondary"
                                    :value="
                                        option.user_account_attribute_definition_value_id
                                    "
                                    :label="option.value"
                                >
                                </v-radio>
                            </v-col>
                        </v-row>
                    </v-radio-group>
                </div>
            </v-col>
        </v-row>
    </b-card>
</template>

<script>
import { mdiCalendarSearch } from "@mdi/js";
import { mapMutations, mapState, mapActions, mapGetters } from "vuex";
import validators from "@/utils/validators/loyaltyPanel/customers";
import Places from "@/components/general/Places.vue";
import translations from "@/utils/translations/loyaltyPanel/customers";
import { cloneDeep } from "lodash";
import moment from "moment";

export default {
    name: "CustomerForm",

    mixins: [validators, translations],

    components: { Places },

    props: {
        modalToggle: Boolean,
        customerData: {
            type: Object,
            default: () => ({
                address: "",
                birth_date: "",
                city: "",
                phone: "",
                mobile: "",
                country_id: "",
                county_id: "",
                email: "",
                first_name: "",
                last_name: "",
                form_date: "",
                store_id: "",
                user_address_id: "",
                zipcode: "",
                gender: "",
                extra_attributes: [],
                renderComponent: true
            })
        },
        isCustomer: Boolean,
        userAccountId: Number
    },

    data() {
        return {
            icons: { mdiCalendarSearch },
            genders: [
                {
                    name: { en: "Male", el: "Άνδρας", it: "Uomo" },
                    value: "male"
                },
                {
                    name: { en: "Female", el: "Γυναίκα", it: "Donna" },
                    value: "female"
                },
                {
                    name: { en: "Other", el: "Άλλο", it: "Altro" },
                    value: "other"
                }
            ],
            pickerDate: "1995-1-1",
            menu: false,
            countryError: {
                el: "Απαιτείται χώρα",
                en: "Country is required",
                it: "Il paese è richiesto"
            },
            errorMsg: { country: "" },
            activeAttributes: [],
            customerAttributes: [],
            initialAttrs: [],
            activePicker: null,
            moment
        };
    },
    computed: {
        ...mapState(["loading", "errorMessage"]),
        ...mapState("storePanel/transactions", ["attributes"]),
        ...mapGetters(["activeCountries", "systemNotifications"]),

        countryCodes() {
            return this.activeCountries.map(e => {
                e = { ...e, countryCode: e.name + " - (" + e.phone_code + ")" };
                return e;
            });
        },
        counties() {
            return this.activeCountries.length && this.customerData.country_id
                ? this.activeCountries.find(
                      country =>
                          country.country_id === this.customerData.country_id
                  ).counties
                : [];
        },

        dateLang() {
            let lang = "en-US";
            if (this.lang === "el") {
                lang = "el-GR";
            } else if (this.lang === "it") {
                lang = "it-IT";
            }
            return lang;
        },

        dateFormatted: {
            get: function() {
                return this.parseDate(this.customerData.form_date);
            },
            set: function(newValue) {
                if (!newValue) this.customerData.form_date = "";
            }
        },

        birthdateFormatted: {
            get: function() {
                return this.parseDate(this.customerData.birth_date);
            },
            set: function(newValue) {
                if (!newValue) this.customerData.birth_date = "";
            }
        },

        lang() {
            return this.$route.params.lang;
        },

        query() {
            if (this.search) {
                return `?q=${this.search}&page=${this.page}`;
            }

            return `?page=${this.page}`;
        }
    },
    watch: {
        menu(val) {
            val &&
                setTimeout(
                    () => (
                        (this.activePicker = "YEAR"), (this.pickerDate = null)
                    )
                );
        },

        modalToggle() {
            this.setErrorMessage("");
        },

        "customerData.country_id": {
            handler(val) {
                if (!val) {
                    this.customerData.county_id = "";
                    // this.$refs.places.clearInput();
                }
            }
        },

        customerData: {
            immediate: true,
            handler(val) {
                for (let key in val) {
                    if (this.customerData[key] == null)
                        this.customerData[key] = "";
                }

                if (!val.extra_attributes?.length) {
                    this.attributes.forEach(e => {
                        e.values = [];
                    });

                    this.activeAttributes.forEach(e => {
                        e.values = [];
                    });
                }
            },
            deep: true
        },

        customerAttributes: {
            deep: true,
            immediate: true,
            handler(val) {
                if (val?.length && this.activeAttributes?.length) {
                    val.forEach((e, i) => {
                        let attr = this.activeAttributes.find(
                            x =>
                                e.user_account_attribute_definition_id ==
                                x.user_account_attribute_definition_id
                        );
                        let defValue = attr.user_account_attribute_definition_values.find(
                            x =>
                                x.user_account_attribute_definition_value_id ==
                                e.user_account_attribute_definition_value_id
                        );
                        e.value = defValue?.value || e.value || null;
                        e.user_account_attribute_definition_value_id =
                            e.user_account_attribute_definition_value_id ||
                            defValue?.user_account_attribute_definition_value_id ||
                            null;

                        if (
                            (attr.attribute_type == "text" ||
                                (e.value &&
                                    !e.user_account_attribute_definition_value_id)) &&
                            !attr.values?.includes(e.value)
                        ) {
                            attr.values.push(e.value);
                        } else if (
                            attr.attribute_type != "text" &&
                            e.user_account_attribute_definition_value_id &&
                            !attr.values?.includes(
                                e.user_account_attribute_definition_value_id
                            )
                        ) {
                            attr.values.push(
                                e.user_account_attribute_definition_value_id
                            );
                        }
                    });
                } else if (this.activeAttributes?.length) {
                    this.activeAttributes.forEach((e, i) => {
                        e.values = [];
                    });
                }
                if(!this.userAccountId){
                    this.customerData.extra_attributes = cloneDeep(val)
                }

            }
        },

        "customerData.extra_attributes": {
            deep: true,
            immediate: true,
            handler(val) {
                this.$nextTick(() => {
                    if (val?.length) {
                        this.initialAttrs = []
                        val.forEach((e, i) => {
                            if (
                                ![null, undefined].includes(
                                    e
                                        .selected_user_account_attribute_definition_values
                                        ?.user_account_attribute_definition_value
                                )
                            ) {
                            
                                this.initialAttrs.push({
                                    user_account_attribute_definition_id:
                                        e.user_account_attribute_definition_id,
                                    value:
                                        e
                                            .selected_user_account_attribute_definition_values
                                            ?.user_account_attribute_definition_value ||
                                        "",
                                    user_account_attribute_definition_value_id:
                                        e
                                            .selected_user_account_attribute_definition_values
                                            ?.user_account_attribute_definition_value_id ||
                                        null
                                });
                            }
                        });
                    }
                
                });
            },
        },
        initialAttrs: {
            immediate: true,
            handler(val) { 
                if (val?.length && this.userAccountId) {
                    this.customerAttributes = cloneDeep(val);
                }
              },
            deep: true
        },
        attributes: {
            immediate: true,
            handler(val) {
                this.activeAttributes = [];
                if (val?.length) {
                    val.forEach(e => {
                        if (e.active) {
                            this.activeAttributes.push({
                                ...e,
                                values: e.values || []
                            });
                        }
                    });
                }
            },
            deep: true
        },
        
    },
    methods: {
        ...mapActions(["loadActiveCountries"]),
        ...mapMutations(["setErrorMessage"]),
        ...mapActions("storePanel/transactions", ["updateCustomer"]),
        parseDate(date) {
            if (!date) return "";
            return new Date(date).toLocaleDateString(this.dateLang, {
                year: "numeric",
                month: "long",
                day: "numeric"
            });
        },
        async saveCustomerDetails(data) {
            let reqBody = { ...data };
            reqBody.customerData.extra_attributes = this.customerAttributes;
            delete reqBody.mobile;
            delete reqBody.store_id;
            delete reqBody.user_address_id;
            await this.updateCustomer(reqBody);
            this.closeModal();
        },

        save(date) {
            this.$refs.menu?.save(date);
            this.pickerDate = date;
        },

        setAddressDetails(place) {
            this.customerData.address = place.address;
        },

        closeModal() {
            this.setErrorMessage("");
            this.$emit("close");
        },

        validateCountry() {
            if (!this.customerData.country_id) {
                this.errorMsg.country = this.countryError[this.lang];
            } else {
                this.errorMsg.country = "";
            }
        },
        async forceRerender() {
            this.renderComponent = false;
            await this.$nextTick();
            this.renderComponent = true;
        },
        attachExtraAttribute(id, vals, isText, hasError) {
            if (!this.customerData?.extra_attributes?.length) {
                this.customerData.extra_attributes = [];
            }
           let customerAttribute = this.customerAttributes.find(
                        e => e.user_account_attribute_definition_id == id
                    ) || { user_account_attribute_definition_id: id };
            
            this.customerAttributes = this.customerAttributes.filter(
                e => e.user_account_attribute_definition_id != id
            );

            vals.forEach(val => {
                if (val && !hasError) {
                    
                    if (isText) {
                        customerAttribute.value = val;
                    } else {
                        customerAttribute.user_account_attribute_definition_value_id = val;
                    }
                    this.customerAttributes.push({...customerAttribute});
                }
            });

        }
    },
};
</script>
